import React, { useEffect, useState } from 'react';
import { ReactStripeElements } from 'react-stripe-elements';
import { useBreakpoints } from 'kb-components/core/useBreakpoints';

import useUnappliedPayments from './hooks/use-unapplied-payments';
import useInvoices from './hooks/use-invoices';
import useTotalBalance from './hooks/use-total-balance';
import usePayInvoice from './hooks/use-pay-invoice';
import InvoiceCardList from './components/InvoiceCardList';
import UnappliedPaymentCardList from './components/UnappliedPaymentCardList';
import Table from './components/Table';
import TotalBalance from './components/TotalBalance';
import Pagination from './components/Pagination';
import AzaleaPayButton from './components/AzaleaPayButton';
import InvoicePayModal from './components/InvoicePayModal';
import AzaleaPayModal from './components/AzaleaPayModal';
import UnappliedPaymentsTable from './components/UnappliedPaymentsTable';
import * as Styled from './Invoices.styled';

const PAGE_SIZE = 10;

interface Props {
  stripe: ReactStripeElements.StripeProps | null;
}

function Invoices({ stripe }: Props) {
  const urlParams = new URLSearchParams(window.location.search);
  const includeAzInvoices = urlParams.get('include_az_invoices') == 'true';
  const {
    loading: invoicesLoading,
    invoices,
    currentPage,
    onNextPage,
    onPreviousPage,
    refetch: refetchInvoices
  } = useInvoices(PAGE_SIZE, includeAzInvoices);
  const { totalBalance, loading: totalBalanceLoading } = useTotalBalance();
  const {
    invoiceToPay,
    setInvoiceToPay,
    payInvoice,
    closeModal,
    isModalOpen: payInvoiceModalOpen
  } = usePayInvoice(stripe);
  const {
    unappliedPayments,
    loading: unappliedPaymentsLoading,
    refetch: refetchUnappliedPayments
  } = useUnappliedPayments();
  const { isMobile } = useBreakpoints();
  const [paymentWithoutInvoiceModalOpen, setPaymentWithoutInvoiceModalOpen] = useState(false);

  useEffect(() => {
    if (window.analytics) {
      window.analytics.page('Invoices');
    }
  }, []);

  useEffect(() => {
    if (payInvoiceModalOpen || paymentWithoutInvoiceModalOpen) return;

    refetchUnappliedPayments();
    refetchInvoices();
  }, [payInvoiceModalOpen, paymentWithoutInvoiceModalOpen]);

  if (invoicesLoading || unappliedPaymentsLoading) {
    return <Styled.Loader />;
  }

  return (
    <Styled.Container>
      <Styled.FlexContainer>
        <Styled.Header>
          <Styled.Title>Your Statements & Payments</Styled.Title>
          <Styled.Description>
            Kindbody recently transitioned to a new billing system. For Dates of Service on or after
            2/15/22, your itemized statements can be viewed in the{' '}
            <a href="/documents">Documents - Kindbody Uploads</a>. We are currently working on a
            simplified user experience to go-live this summer. Any questions? Call your patient
            navigator at 1-855-KND-BODY or send your billing team a secure message in the portal.
          </Styled.Description>
        </Styled.Header>

        <Styled.TotalBalanceAndPayButtonHolder>
          <TotalBalance value={totalBalance} loading={totalBalanceLoading} />
          {!includeAzInvoices && (
            <AzaleaPayButton onClick={() => setPaymentWithoutInvoiceModalOpen(true)}>
              Make a payment
            </AzaleaPayButton>
          )}
        </Styled.TotalBalanceAndPayButtonHolder>

        {includeAzInvoices && (
          <>
            <Styled.SubTitle>Unapplied Payments</Styled.SubTitle>
            {isMobile ? (
              <UnappliedPaymentCardList payments={unappliedPayments} />
            ) : (
              <UnappliedPaymentsTable payments={unappliedPayments} />
            )}
            <Styled.SubTitle>Invoices</Styled.SubTitle>
          </>
        )}
        {isMobile ? (
          <InvoiceCardList invoices={invoices} onPayInvoice={setInvoiceToPay} />
        ) : (
          <Table invoices={invoices} onPayInvoice={setInvoiceToPay} />
        )}

        {invoices.length > 0 && (
          <Pagination
            currentPage={currentPage}
            onNextClick={onNextPage}
            onPreviousClick={onPreviousPage}
          />
        )}
      </Styled.FlexContainer>

      <InvoicePayModal
        invoice={invoiceToPay}
        open={payInvoiceModalOpen}
        onPayInvoice={payInvoice}
        onClose={closeModal}
      />
      <AzaleaPayModal
        open={paymentWithoutInvoiceModalOpen}
        stripe={stripe}
        onClose={() => setPaymentWithoutInvoiceModalOpen(false)}
      />
    </Styled.Container>
  );
}

export default Invoices;
